/**
 * Active-org switch orchestrator (web). Mirrors mobile's
 * `src/features/org/setActiveOrg.ts` so the switch sequence is
 * identical across platforms.
 *
 * Sequence:
 *
 *   1. Server: `setPrimaryOrganization` callable writes
 *      `users/{uid}.primaryOrgId` AND calls `refreshUserClaims` to
 *      mint fresh custom claims with the new active org.
 *      ~200ms round-trip.
 *
 *   2. Client (cheap barrier ~150ms): wait for the local
 *      `users/{uid}` snapshot to reflect the new `primaryOrgId`. The
 *      snapshot fires shortly after the server write and unblocks
 *      `useFullOrganization` (and every downstream hook) to switch
 *      over to the new-org data before the dialog closes. Without
 *      this, the next render briefly trusts the OLD primaryOrgId
 *      and shows the previous org's projects / quotes / etc.
 *
 *   3. Client (background): fire-and-forget `refresh(true)` to pull
 *      the new token claims into the local cache. Not awaited —
 *      the userDoc barrier already gave us the new primaryOrgId in
 *      step 2.
 *
 * Why both barriers: blocking on `refresh` alone adds 1–2s of
 * perceived latency (the token round-trip is the slowest). Blocking
 * on `waitForUserDoc` alone is ~150ms and gives us the correct
 * primaryOrgId immediately via the userDoc snapshot. So we await the
 * cheap one and let the expensive one run in the background.
 */
import { setPrimaryOrganization } from '@/lib/organizations';
import type { TokenClaims } from '@/lib/useTokenClaims';

export type SetActiveOrgArgs = {
  /** Optional handle to `useTokenClaims().refresh`. When provided we
   *  kick off a token refresh in the background; not awaiting keeps
   *  the switch fast. */
  refresh?: (force?: boolean) => Promise<TokenClaims>;
  /** Optional barrier that resolves once the local `users/{uid}`
   *  snapshot reports `primaryOrgId === orgId`. Caller wires it to a
   *  one-shot Firestore listener (see `OrgSwitcherDialog`). Capped
   *  to a short timeout so a stuck snapshot doesn't hang the switch. */
  waitForUserDoc?: () => Promise<void>;
};

const USER_DOC_BARRIER_TIMEOUT_MS = 1500;

export async function setActiveOrg(
  orgId: string,
  args: SetActiveOrgArgs = {},
): Promise<void> {
  if (!orgId) throw new Error('setActiveOrg: orgId is required');

  // Server: write user.primaryOrgId + refreshUserClaims.
  await setPrimaryOrganization(orgId);

  // Client barrier (cheap, ~150ms): wait for userDoc snapshot to
  // reflect the new primaryOrgId before returning. Bounded by a
  // short timeout — if stuck, fall through and let downstream hooks
  // catch up on the next snapshot.
  if (args.waitForUserDoc) {
    try {
      await Promise.race([
        args.waitForUserDoc(),
        new Promise<void>((resolve) =>
          setTimeout(resolve, USER_DOC_BARRIER_TIMEOUT_MS),
        ),
      ]);
    } catch (err) {
      console.warn('[setActiveOrg] userDoc barrier failed:', err);
    }
  }

  // Client: pull new claims in the background. Don't await — the
  // userDoc barrier already gave us the new primaryOrgId.
  if (args.refresh) {
    void args.refresh(true).catch((err) => {
      console.warn('[setActiveOrg] background claims refresh failed:', err);
    });
  }
}
